import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import useQueues from '~/hooks/useQueues';
import { DataItem } from '~components/DataItem';
import ControlledNumberField from '~components/Form/ControlledNumberField';
import SectionCard from '~components/SectionCard';
import { DiallerGroupPredictiveSettings, UpdateDiallerGroup } from '~pages/CampaignManagement/domain';
import { deepCompare } from '~utils/Functions';

interface PredictiveSettingsFormProps {
  predictiveSettings: DiallerGroupPredictiveSettings;
  isConnectPredictive: boolean;
  isEdit: boolean;
  submitting: boolean;
  toggleEdit?: () => void;
  update: (data: Partial<UpdateDiallerGroup>) => Promise<void>;
}

interface Form {
  ringTimeSeconds: number;
  holdDropSeconds: number;
  maxLinesPerAgent: number;
  lookaheadTimeSeconds: number;
  numReservedAgents: number;
  outboundQueue: string | null | undefined;
}

const PredictiveSettingsForm = ({
  predictiveSettings,
  isConnectPredictive,
  isEdit,
  submitting,
  toggleEdit,
  update,
}: PredictiveSettingsFormProps) => {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { loading, queues } = useQueues();
  const isLoading = loading || submitting;
  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
    watch,
  } = useForm<Form>({
    defaultValues: {
      ringTimeSeconds: 0,
      holdDropSeconds: 0,
      maxLinesPerAgent: 0,
      lookaheadTimeSeconds: 0,
      numReservedAgents: 0,
      outboundQueue: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const disablePerformAction =
    predictiveSettings &&
    deepCompare(predictiveSettings.ringTimeSeconds, watch('ringTimeSeconds')) &&
    deepCompare(predictiveSettings.holdDropSeconds, watch('holdDropSeconds')) &&
    deepCompare(predictiveSettings.maxLinesPerAgent, watch('maxLinesPerAgent')) &&
    deepCompare(predictiveSettings.lookaheadTimeSeconds, watch('lookaheadTimeSeconds')) &&
    deepCompare(predictiveSettings.numReservedAgents, watch('numReservedAgents')) &&
    deepCompare(predictiveSettings.outboundQueue, watch('outboundQueue'));

  useEffect(() => {
    if (isEdit) {
      reset({
        ringTimeSeconds: predictiveSettings.ringTimeSeconds,
        holdDropSeconds: predictiveSettings.holdDropSeconds,
        maxLinesPerAgent: predictiveSettings.maxLinesPerAgent,
        lookaheadTimeSeconds: predictiveSettings.lookaheadTimeSeconds,
        numReservedAgents: predictiveSettings.numReservedAgents,
        outboundQueue: predictiveSettings.outboundQueue,
      });
    }

    return () => {
      reset();
    };
  }, [isEdit]);

  const onSubmit = handleSubmit(async (data: Form) => {
    const submitData: Partial<UpdateDiallerGroup> = {
      predictiveSettings: {
        ringTimeSeconds: data.ringTimeSeconds,
        holdDropSeconds: data.holdDropSeconds,
        maxLinesPerAgent: data.maxLinesPerAgent,
        lookaheadTimeSeconds: data.lookaheadTimeSeconds,
        numReservedAgents: data.numReservedAgents,
        outboundQueue: data.outboundQueue,
      },
    };

    update(submitData);
  });

  let outboundQueueName = predictiveSettings.outboundQueue
    ? queues[predictiveSettings.outboundQueue]?.name
    : predictiveSettings.outboundQueue;

  const queueIdList = Object.keys(queues).sort((a, b) => {
    let an = queues[a]?.name;
    let bn = queues[b]?.name;
    return an == bn ? 0 : an > bn ? 1 : -1;
  });

  return (
    <SectionCard title='Predictive Settings' onEdit={toggleEdit}>
      {!isEdit && (
        <>
          <DataItem
            stacked={isExtraSmall}
            disableMargin
            title='Ring time seconds'
            tooltip='The amount of seconds for ring time. Once this time is exhausted, the dialler will hang up.'
            value={predictiveSettings.ringTimeSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Hold drop seconds'
            tooltip='The number of seconds to wait for a subsequent agent to become available (if the initial agent is unavailable) before a call is disconnected.'
            value={predictiveSettings.holdDropSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Max lines per agent'
            tooltip='The number of leads that can be processed simultaneously. This setting defines how aggressive the predictive dialling strategy is. The higher the number, the more aggressive the strategy will be.'
            value={predictiveSettings.maxLinesPerAgent}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Lookahead time seconds'
            tooltip='The amount of seconds the dialler can look ahead to predict when an agent will be available and therefore assign and dial the next lead.'
            value={predictiveSettings.lookaheadTimeSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Number reserved agents'
            tooltip='The number of agents to reserve for inbound return calls only.'
            value={predictiveSettings.numReservedAgents}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Outbound queue'
            tooltip='Used to know how many agents are available for calls and how many calls are in-flight that may take priority.'
            value={outboundQueueName}
          />
        </>
      )}
      {isEdit && (
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid size={12}>
              <ControlledNumberField
                name='ringTimeSeconds'
                control={control}
                rules={{
                  required: 'ring time seconds is required.',
                }}
                label='Ring time seconds'
                disabled={isLoading}
                required={true}
                error={Boolean(errors.ringTimeSeconds)}
                helperText={errors.ringTimeSeconds?.message}
              />
            </Grid>

            <Grid size={12}>
              <ControlledNumberField
                name='holdDropSeconds'
                control={control}
                rules={{
                  required: 'hold drop seconds is required.',
                }}
                label='Hold drop seconds'
                disabled={isLoading}
                required={true}
                error={Boolean(errors.holdDropSeconds)}
                helperText={errors.holdDropSeconds?.message}
              />
            </Grid>

            <Grid size={12}>
              <ControlledNumberField
                name='maxLinesPerAgent'
                control={control}
                rules={{
                  required: 'max lines per agent is required.',
                }}
                label='Max lines per agent'
                disabled={isLoading}
                required={true}
                isFloat={true}
                error={Boolean(errors.maxLinesPerAgent)}
                helperText={errors.maxLinesPerAgent?.message}
              />
            </Grid>

            <Grid size={12}>
              <ControlledNumberField
                name='lookaheadTimeSeconds'
                control={control}
                rules={{
                  required: 'lookahead time seconds is required.',
                }}
                label='Lookahead time seconds'
                disabled={isLoading}
                required={true}
                error={Boolean(errors.lookaheadTimeSeconds)}
                helperText={errors.lookaheadTimeSeconds?.message}
              />
            </Grid>

            <Grid size={12}>
              <ControlledNumberField
                name='numReservedAgents'
                control={control}
                rules={{
                  required: 'number of reserved agents is required.',
                }}
                label='Number of reserved agents'
                disabled={isLoading}
                required={true}
                error={Boolean(errors.numReservedAgents)}
                helperText={errors.numReservedAgents?.message}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name='outboundQueue'
                control={control}
                rules={isConnectPredictive ? { required: 'Outbound queue is required' } : {}}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    fullWidth
                    onChange={(e, data) => {
                      field.onChange(data);
                    }}
                    options={queueIdList}
                    disabled={isLoading}
                    disableClearable={isConnectPredictive}
                    getOptionLabel={(option) => queues[option]?.name || ''}
                    renderOption={({ key, ...optionProps }, option) => (
                      <li {...optionProps} key={option}>
                        <Typography variant='body1' color='textPrimary' component='p'>
                          {queues[option]?.name}
                        </Typography>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Outbound queue'
                        variant='outlined'
                        required={true}
                        error={Boolean(errors.outboundQueue)}
                        helperText={errors.outboundQueue?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid sx={{ textAlign: 'right' }} size={12}>
              <Button onClick={toggleEdit}>Cancel</Button>

              <LoadingButton
                sx={{ marginLeft: 1 }}
                type='submit'
                variant='contained'
                disableElevation
                disabled={disablePerformAction}
                loading={isLoading}
                color='primary'>
                Update
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      )}
    </SectionCard>
  );
};

export default PredictiveSettingsForm;
